import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  compactBotsListForPersist,
  fetchBotsListForRegistry,
  patchBotConfigUrlInMemory,
  setBotConfigUrlInBotsList,
} from '../../../config/botsListRegistry';
import { getBotRoutePath, normalizeDirectUrlPath } from '../../../config/getBotConfig';
import { saveBotConfig, saveBotsList } from '../../../services/admin/botAdminApi';
import SkillLmProjectSelect from '../shared/skillLmProjectSelect/SkillLmProjectSelect';
import SubjectSelect from '../subjectSelect/SubjectSelect';
import classes from './CreateNewBotForm.module.css';

const SAFE_NAME = /^[a-zA-Z0-9_-]+$/;
const SAFE_URL_PATH = /^[a-zA-Z0-9_/-]+$/;

const DEFAULT_THEME_COLORS = {
  backgroundGradientStart: '#E3EFF9',
  backgroundGradientEnd: '#CCDBF4',
  backgroundGradientSidebarStart: '#E2EEF8',
  backgroundGradientSidebarEnd: '#E2EEF8',
  headerGradientEnd: '#CDDBF4',
  headerGradientMid: '#CDDBF4',
  headerGradientStart: '#CDDBF4',
  surface: '#ffffff',
  cookiesSurface: '#ffffff',
  primary: '#9C3078',
  onPrimary: '#ffffff',
  userBubbleBackground: '#9C3078',
  userBubbleText: '#FAFAFA',
  modelBubbleBackground: '#FAFAFA',
  modelBubbleText: '#262428',
  loaderPrimary: '#9C3078',
  loaderSecondary: '#E3EFF9',
  discoveryButtonBorder: '#9C3078',
  discoveryButtonBackground: '#ffffff',
  discoveryButtonText: '#2c1952',
  sessionItemSelectedBackground: '#F1D0E6',
  sessionItemDateText: '#7E7986',
  sessionItemTopicText: '#262428',
  sendButtonDisabledBackground: '#FAFAFA',
  messageInputBackground: '#FAFAFA',
  messageInputBorder: '#B1AFB6',
  sidebarFooterBorder: '#D4DEF5',
  sidebarFooterText: '#9C3078',
  sidebarHeaderText: '#262428',
  iconCloseStroke: '#000000',
  iconDragStroke: '#000000',
  iconChatsStroke: '#9C3078',
  iconExpandStroke: '#030303',
  iconSidebarCollapseStroke: '#9C3078',
  iconSidebarFooterStroke: '#ffffff',
  iconSendStroke: '#FAFAFA',
  authButtonText: '#ffffff',
  iconSendFill: '#9C3078',
  iconChatTextStroke: '#ffffff',
  iconNewConversationStroke: '#9C3078',
  greetingText: '#323036',
};

export default function CreateNewBotForm() {
  const navigate = useNavigate();
  const { texts } = useSelector((state) => state.translate);
  const [domainName, setDomainName] = useState('');
  const [subjectName, setSubjectName] = useState('');
  const [subjectImageUrl, setSubjectImageUrl] = useState('');
  const [botName, setBotName] = useState('');
  const [urlPath, setUrlPath] = useState('');
  const [agentId, setAgentId] = useState('');
  const [skillLmProject, setSkillLmProject] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    const domain = domainName.trim();
    const bot = botName.trim();
    const path = normalizeDirectUrlPath(urlPath.trim());
    const agent = agentId.trim();
    const project = skillLmProject.trim();

    if (!SAFE_NAME.test(domain) || !SAFE_NAME.test(bot)) {
      setError(texts.createBotInvalidName);
      return;
    }
    if (path && !SAFE_URL_PATH.test(path)) {
      setError(texts.createBotInvalidUrlPath);
      return;
    }
    if (window.config?.botsList?.[domain]?.bots?.[bot]) {
      setError(texts.createBotExists);
      return;
    }

    const baseApiUrl = (window.config?.baseApiUrl || '').replace(/\/$/, '');
    if (!baseApiUrl) {
      setError(texts.createBotFailed);
      return;
    }

    setSaving(true);
    setError('');

    const content = {
      theme: { colors: { ...DEFAULT_THEME_COLORS } },
      botTitle: bot,
      botSubTitle: '',
      welcomeMessage: '',
      nonAuthText: '',
      typeHereText: '',
      discoveryButton: [],
      activeInMyEdu: false,
      key: bot,
    };
    if (subjectName) content.subjectName = subjectName;
    if (subjectImageUrl) content.subjectImageUrl = subjectImageUrl;
    if (agent) content.agentId = agent;
    if (project) content.skillLmProject = project;

    try {
      const configRes = await saveBotConfig({
        domainName: domain,
        botName: bot,
        content,
      });
      if (!configRes?.ok) {
        throw new Error(texts.createBotFailed);
      }

      const source = await fetchBotsListForRegistry();
      const { botsList } = setBotConfigUrlInBotsList(source, domain, bot, { urlPath: path });
      const registryRes = await saveBotsList({
        directory: 'bots',
        fileName: 'botsList.json',
        content: compactBotsListForPersist(botsList),
      });
      if (!registryRes?.ok) {
        throw new Error(texts.createBotFailed);
      }
      patchBotConfigUrlInMemory(domain, bot, { urlPath: path });

      navigate(`/admin/${getBotRoutePath(domain, bot, path)}`);
    } catch {
      setError(texts.createBotFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={classes.Form} onSubmit={onSubmit}>
      <h2 className={classes.Title}>{texts.createNewBot}</h2>
      <SubjectSelect
        id="create-bot-domain"
        label={texts.createBotDomain}
        domain={domainName}
        subjectName={subjectName}
        subjectImageUrl={subjectImageUrl}
        onSelect={({ domain, subjectName: name, subjectImageUrl: imageUrl }) => {
          setDomainName(domain || '');
          setSubjectName(name || '');
          setSubjectImageUrl(imageUrl || '');
        }}
        disabled={saving}
      />
      <label className={classes.Label} htmlFor="create-bot-name">
        {texts.createBotName}
      </label>
      <input
        id="create-bot-name"
        className={classes.Input}
        value={botName}
        onChange={(e) => setBotName(e.target.value)}
        autoComplete="off"
        disabled={saving}
      />
      <label className={classes.Label} htmlFor="create-bot-urlPath">
        {texts.createBotUrlPath}
      </label>
      <input
        id="create-bot-urlPath"
        className={classes.Input}
        value={urlPath}
        onChange={(e) => setUrlPath(e.target.value)}
        autoComplete="off"
        disabled={saving}
        placeholder="/unison"
      />
      <label className={classes.Label} htmlFor="create-bot-agent">
        {texts.createBotAgentId}
      </label>
      <input
        id="create-bot-agent"
        className={classes.Input}
        value={agentId}
        onChange={(e) => setAgentId(e.target.value)}
        autoComplete="off"
        disabled={saving}
      />
      <SkillLmProjectSelect
        id="create-bot-skillLmProject"
        label={texts.createBotSkillLmProject}
        value={skillLmProject}
        onChange={setSkillLmProject}
        disabled={saving}
      />
      {error ? <p className={classes.Error} role="alert">{error}</p> : null}
      <button type="submit" className={classes.Submit} disabled={saving}>
        {saving ? texts.creatingBot : texts.createBotSubmit}
      </button>
    </form>
  );
}
